import { useCallback, useContext, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Link, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { DynamicOption } from '@refineintel/types';
import type { V2AutocompleteElement } from '../../../types/V2Types';
import type { V2ElementRendererProps } from './elementProps';
import { useV2DynamicOptions } from '../../../hooks/useV2DynamicOptions';
import { RDropdown } from '../../RAutocomplete/RDropdown';
import { LocalizationContext } from '@context/LocalizationContext';

const DEFAULT_PAGE_SIZE = 50;
const NAICS_LINK = 'https://www.naics.com/search/';

/**
 * HRR v2 searchable autocomplete renderer (RND-4221).
 *
 * The BE ships these elements with `options: { dynamicOptionsType: "naics" }`
 * (an object, not an array) because the option list is too large to inline
 * (NAICS = thousands of codes). `adaptElement` routes them here as
 * `V2AutocompleteElement`. We fetch matching options on demand from
 * `GET /getDynamicOptions?type=<dynamicOptionsType>` via `useV2DynamicOptions`
 * (the v2-native sibling of v1's `useQueryGetDynamicOptions`), and render the
 * proven v1 typeahead UI (`RDropdown`).
 *
 * Wire format: on select we persist the option's `label` (the human-readable
 * "<code> <title>" string, e.g. "541511 Custom Computer Programming Services - T")
 * as a plain `string`. v2 answers are string-only (`V2ElementValue`), and on
 * goBack the BE echoes back only that bare string. Persisting the label (rather
 * than the option's internal UUID `id`) means: (a) it is self-contained and
 * human-readable, so the eddHistory "Client answered" value renders the label
 * not an opaque UUID; and (b) it can be re-displayed verbatim on rewind without
 * a second lookup. (RND-4221 — the old `option.id` persisted the dynamic-option
 * UUID, which displayed as a UUID and could not be searched back.)
 *
 * goBack pre-fill: the incoming `value` is the previously-stored label string.
 * We hand `RDropdown` a `DynamicOption` whose `label` IS that string, so the
 * input shows the full human label on rewind. We also seed the search query
 * from the *code* embedded in the label (its first whitespace token) so the BE
 * re-fetches the matching row(s) and the reopened dropdown shows a populated,
 * selectable list. Typing then re-searches normally.
 *
 * Free-text mode (RND-4217): with `allowFreeText` (address fields) the typed
 * text is committed as the answer on every keystroke and the fetched options
 * are assistive suggestions only — selecting one replaces the text. See
 * `V2AutocompleteElement.allowFreeText` and the `freeText` prop on RDropdown.
 *
 * data-cy: inherits `pqDropdownContainer` / `pqDropdownInput` from RDropdown;
 * adds a wrapping `hrr-element-${elementId}` for E2E parity with sibling
 * v2 renderers.
 */
export function AutocompleteElement({
	element,
	value,
	errors,
	disabled,
	onChange,
}: V2ElementRendererProps<V2AutocompleteElement>) {
	// The previously-stored answer is the option's human-readable label, e.g.
	// "541511 Custom Computer Programming Services - T" (see handleSelect). Empty
	// string / non-string means "not answered yet" (forward entry).
	const initialLabel = typeof value === 'string' && value.length > 0 ? value : '';
	const errorMessage = errors[0]?.message;
	const hasError = errors.length > 0;

	const freeText = element.allowFreeText === true;

	// Seed the search query from the *code* embedded in the stored label (its
	// first whitespace token, e.g. "541511" or sector "11"). The only fetch
	// trigger in useV2DynamicOptions is the (debounced) searchQuery effect; the
	// mount fetch runs with an empty query and its page-1 list never contains the
	// user's specific stored NAICS code, so reopening the field on goBack would
	// show a dropdown that lacks their value. The BE search (`getDynamicOptions`
	// -> `getByKeyOrValueWithPagination`) matches `key contains <q> OR value
	// contains <q>` (case-insensitive), and the option's `key` IS that code, so
	// seeding the code re-fetches the matching row(s) at this position — the
	// stored option is then present + selectable in the reopened list (true for
	// both the sector case and a deep-code case). Forward entry is unchanged
	// (value null -> initialLabel '' -> initialSearch '' -> identical empty-query
	// page-1 fetch). This effect never calls onChange, so the restored answer is
	// not clobbered. (RND-4221 goBack)
	//
	// Free-text (RND-4217): the stored value is the customer's verbatim string
	// (an address), not a "<code> <title>" label — there is no code token to
	// extract, so seed the search with the whole string.
	const initialSearch = freeText ? initialLabel : (initialLabel.split(/\s+/)[0] ?? '');
	const [searchQuery, setSearchQuery] = useState(initialSearch);
	const [pagination, setPagination] = useState({ currentPage: 1, pageSize: DEFAULT_PAGE_SIZE });

	// NAICS-only helper (matches v1 NAICSQuestion.tsx behaviour): when the dynamic
	// option set is NAICS, surface a "Find code on NAICS directory" affordance
	// below the typeahead. Clicking it opens an info dialog explaining NAICS codes
	// with a link to the public naics.com directory — identical to the legacy v1
	// flow (RND-4268), so OceanFirst (v1) and HRR (v2) present NAICS the same way.
	// Reuses the existing v1 i18n keys so copy stays in sync across PQ surfaces.
	// Other dynamicOptionsType values render the dropdown alone.
	const {
		NAICS: { FindYourNAICSCodeButton, InformationDialogTitle, InformationDialogText },
	} = useContext(LocalizationContext);
	const isNaics = element.dynamicOptionsType === 'naics';
	const [naicsInfoOpen, setNaicsInfoOpen] = useState(false);

	// Seed the typeahead input from the previously-stored label (goBack pre-fill).
	// RDropdown reads `value.label` once for its initial input text, so passing
	// the stored label as `label` makes the field DISPLAY the full human-readable
	// selection ("541511 Custom Computer Programming Services - T") on rewind. The
	// list itself is populated independently by the code-seeded search above, so
	// the displayed text survives in both the sector case and the deep-code case
	// (the label does not need to be in the fetched page for it to render).
	const initialSelected: DynamicOption | null = useMemo(
		() => (initialLabel ? { id: initialLabel, value: initialLabel, label: initialLabel } : null),
		[initialLabel],
	);

	const { options, isLoading, hasMorePages, paginationInfo } = useV2DynamicOptions({
		searchQuery,
		type: element.dynamicOptionsType,
		pagination,
	});

	const handleSelect = useCallback(
		(option: DynamicOption | null) => {
			// Persist the human-readable label ("<code> <title>") as a plain string.
			// Storing the label (not the option's internal UUID `id`) keeps the answer
			// self-contained: it both renders correctly in eddHistory and re-displays
			// verbatim on goBack. Clearing the field stores ''. (RND-4221)
			onChange(option?.label ?? '');
		},
		[onChange],
	);

	const handleLoadMore = useCallback(() => {
		if (hasMorePages && !isLoading) {
			setPagination((prev) => ({ ...prev, currentPage: paginationInfo.currentPage + 1 }));
		}
	}, [hasMorePages, isLoading, paginationInfo.currentPage]);

	const handleSearch = useCallback(
		(next: string) => {
			setSearchQuery(next);
			// Reset paging whenever the query changes so page 1 replaces the list.
			setPagination({ currentPage: 1, pageSize: DEFAULT_PAGE_SIZE });
			// Free-text (RND-4217): the typed text IS the answer — commit it on
			// every keystroke so the customer can submit an address no suggestion
			// matches (and a suggestions outage degrades to a plain text field).
			// Selecting a suggestion still routes through handleSelect and simply
			// replaces this string with the option's label.
			if (freeText) onChange(next);
		},
		[freeText, onChange],
	);

	return (
		<Stack data-cy={`hrr-element-${element.elementId}`} data-element-type="autocomplete" gap={1} width="100%">
			{element.label && (
				<Typography variant="answerText">
					{element.label}
					{element.required ? ' *' : ''}
				</Typography>
			)}
			<RDropdown
				options={options}
				isLoading={isLoading}
				isValid={!hasError}
				required={element.required}
				value={initialSelected}
				placeholder={element.placeholder}
				errorMessage={errorMessage}
				setSearchValue={handleSearch}
				hasMorePages={hasMorePages}
				onScrolledToBottom={handleLoadMore}
				onSelect={disabled ? () => undefined : handleSelect}
				freeText={freeText}
			/>
			{isNaics && (
				<>
					<Typography
						data-cy={'pqNAICSInformationLink'}
						onClick={() => setNaicsInfoOpen(true)}
						sx={{
							fontSize: '12px',
							color: 'primary.main',
							alignSelf: 'flex-start',
							cursor: 'pointer',
							'&:hover': { textDecoration: 'underline' },
						}}>
						{FindYourNAICSCodeButton}
					</Typography>
					<Dialog
						open={naicsInfoOpen}
						onClose={() => setNaicsInfoOpen(false)}
						maxWidth="sm"
						fullWidth
						data-cy={'pqNAICSInformationDialog'}>
						<DialogTitle fontSize={'16px'}>{InformationDialogTitle}</DialogTitle>
						<IconButton
							aria-label="Close"
							onClick={() => setNaicsInfoOpen(false)}
							sx={{ position: 'absolute', top: 8, right: 8 }}>
							<CloseIcon fontSize="small" />
						</IconButton>
						<DialogContent>
							<Stack gap={2.5}>
								<Typography fontSize={'14px'}>{InformationDialogText}</Typography>
								<Link
									data-cy={'pqNAICSDirectoryLink'}
									href={NAICS_LINK}
									target="_blank"
									rel="noopener noreferrer"
									underline="hover"
									sx={{ fontSize: '14px', cursor: 'pointer', wordBreak: 'break-all' }}>
									{NAICS_LINK}
								</Link>
							</Stack>
						</DialogContent>
					</Dialog>
				</>
			)}
			{element.description && (
				<Typography variant="answerText" color="text.secondary">
					{element.description}
				</Typography>
			)}
		</Stack>
	);
}
